import {
  Button,
  CircularProgress,
  FormControl,
  Box,
  OutlinedInput,
  styled,
  Typography,
} from "@mui/material";
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import UserContext from "../../contexts/user";
import { useSnackbar } from "notistack";
import roleRoutes from "../../constants/roleRoutes";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Link } from "react-router-dom";
import Reset from "../auth/Reset";

const StyledFormControl = styled(FormControl)({
  borderRadius: "0.5rem",
  height: "3rem",
  width: "21rem",
  background: "#EEF6FF",
  border: "1px solid #5865F2",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: "transparent",
    },
    "&.Mui-focused fieldset": {
      borderColor: "transparent",
    },
  },
  "& .MuiOutlinedInput-input": {
    display: "flex",
    alignItems: "center",
    color: "#6A6A6A",
    padding: "4px",
    background: "transparent",
  },
});

const StyledButton = styled(Button)({
  backgroundColor: "#5865F2",
  height: "3rem",
  width: "21rem",
  borderRadius: "1.5rem",
  color: "#FFFFFF",
  letterSpacing: "0.078125rem",
});

const Signin = () => {
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    newPassword: "",
    confirmPassword: "",
  });
  const { setUser, setUserRole } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const signIn = async () => {
    setLoading(true);
    try {
      const { email, password } = formData;
      let _user = await Auth.signIn(email, password);
      if (_user.challengeName !== `NEW_PASSWORD_REQUIRED`) {
        setUser(_user.attributes);
        setUserRole(_user.attributes["custom:type"]);
        _user.cacheTokens();
        _user.cacheUserData();
        setLoading(false);
        navigate(roleRoutes[_user.attributes["custom:type"]]);
      } else {
        setCurrentUser(_user);
        setNewUser(true);
        setLoading(false);
      }
    } catch (e) {
      console.log("Signin: Error", e);
      enqueueSnackbar("Incorrect username or password", { variant: "error" });
      setLoading(false);
    }
  };

  const setNewPassword = async () => {
    setLoading(true);
    try {
      const { email, newPassword, confirmPassword } = formData;
      if (newPassword !== confirmPassword) {
        console.log("Set new Password: Error");
        enqueueSnackbar("Passwords do not match!", { variant: "error" });
        setLoading(false);
      } else {
        let _user = await Auth.completeNewPassword(currentUser, newPassword);
        setUser(_user.attributes);
        setUserRole(_user.attributes["custom:type"]);
        _user.cacheTokens();
        _user.cacheUserData();
        setNewUser(false);
        setLoading(false);
        navigate(roleRoutes[_user.attributes["custom:type"]]);
      }
    } catch (e) {
      console.log("Signin: Error", e);
      enqueueSnackbar("Unable to set new password", { variant: "error" });
      setLoading(false);
    }
  };

  return (
    <>
      <StyledFormControl>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <EmailIcon
            sx={{
              height: "1.5rem",
              width: "1.125rem",
              marginLeft: "0.9375rem",
              color: "#5865F2",
            }}
          />
          <OutlinedInput
            placeholder="abcd@gmail.com"
            sx={{
              flex: 1,
            }}
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
        </Box>
      </StyledFormControl>
      {newUser === true ? (
        <>
          <StyledFormControl>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <LockIcon
                sx={{
                  height: "1.5rem",
                  width: "1rem",
                  marginLeft: "1rem",
                  color: "#5865F2",
                }}
              />
              <OutlinedInput
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                sx={{
                  flex: 1,
                }}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
              />
              <VisibilityIcon
                onClick={handleTogglePassword}
                sx={{
                  height: "1.5rem",
                  width: "1.3125rem",
                  marginRight: "1.125rem",
                  color: "#5865F2",
                  cursor: "pointer",
                }}
              />
            </Box>
          </StyledFormControl>
          <StyledFormControl>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <LockIcon
                sx={{
                  height: "1.5rem",
                  width: "1rem",
                  marginLeft: "1rem",
                  color: "#5865F2",
                }}
              />
              <OutlinedInput
                type={showPassword ? "text" : "password"}
                placeholder="Confirm Password"
                sx={{
                  flex: 1,
                }}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              <VisibilityIcon
                onClick={handleTogglePassword}
                sx={{
                  height: "1.5rem",
                  width: "1.3125rem",
                  marginRight: "1.125rem",
                  color: "#5865F2",
                  cursor: "pointer",
                }}
              />
            </Box>
          </StyledFormControl>
          <StyledButton
            onClick={setNewPassword}
            sx={{ mt: 1 }}
            variant="contained"
          >
            {loading ? (
              <CircularProgress size={"1.4rem"} color="inherit" />
            ) : (
              "Set New Password"
            )}
          </StyledButton>
        </>
      ) : (
        <>
          <StyledFormControl>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <LockIcon
                sx={{
                  height: "1.5rem",
                  width: "1rem",
                  marginLeft: "1rem",
                  color: "#5865F2",
                }}
              />
              <OutlinedInput
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                sx={{
                  flex: 1,
                }}
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              <VisibilityIcon
                onClick={handleTogglePassword}
                sx={{
                  height: "1.5rem",
                  width: "1.3125rem",
                  marginRight: "1.125rem",
                  color: "#5865F2",
                  cursor: "pointer",
                }}
              />
            </Box>
          </StyledFormControl>
          <Link style={{ width: "100%" }} to={"/login/reset"}>
            <Typography
              sx={{
                textAlign: "right",
                color: "#5865F2",
                fontSize: "0.875rem",
                fontWeight: 700,
                letterSpacing: "0.078125rem",
              }}
            >
              Forgot Password?
            </Typography>
          </Link>
          <StyledButton onClick={signIn} sx={{ mt: 1 }} variant="contained">
            {loading ? (
              <CircularProgress size={"1.4rem"} color="inherit" />
            ) : (
              "Login"
            )}
          </StyledButton>
        </>
      )}
    </>
  );
};

export default Signin;
